import React, { useMemo } from "react";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";

import { Outlet } from "react-router-dom";
import ResponsiveAppBar from "../AdminAppBar";
import {
  usersSliceSelector,
  initAuth,
  logout,
} from "../../redux/features/usersSlice";
import {
  organizationsSliceSelector,
  changeOrganization,
  setSnoozedInvitation,
  setSnoozedOwnershipTransfer,
} from "../../redux/features/organizationsSlice";
import { beginListeningToUnreviewedDraftsCount } from "../../redux/features/performersSlice";
import { pages } from "./pages";
import logger from "../../utils/logger";
import { CRM_ORG_ID, DEV_TOOLS_ORG_ID } from "../AdminAppBar/ResponsiveAppBar";

function Admin() {
  const dispatch = useDispatch();
  const usersState = useSelector((state) => usersSliceSelector(state));
  const organizationsState = useSelector((state) =>
    organizationsSliceSelector(state),
  );
  const location = useLocation();
  const navigate = useNavigate();
  const pathIsLogin = location.pathname === "/login";
  const pathIsBase = location.pathname === "/";
  const isCheckInView =
    location.hash === "#checkIn" &&
    /^\/events\/[^/]+\/tickets/.test(location.pathname);
  const { user, authState } = usersState;
  const {
    currentOrganization,
    organizations,
    organizationsInitialized,
    myInvitations,
    myOwnershipTransfers,
  } = organizationsState;
  const pageList = useMemo(
    () => pages({ currentOrganization }),
    [currentOrganization],
  );
  const handleLogoutClick = () => {
    dispatch(logout());
  };

  const handleClickNewOrganization = () => {
    navigate("/organizations/new");
  };
  const handleChangeOrganization = (organizationId) => {
    dispatch(changeOrganization({ organizationId }));
    if (organizationId === CRM_ORG_ID) {
      navigate("/crm");
    } else if (organizationId === DEV_TOOLS_ORG_ID) {
      navigate("/dev-tools");
    } else if (organizationId) {
      // Navigate to dashboard when selecting an organization
      navigate("/dashboard");
    }
  };

  const handleClickInvitation = (invitationId) => {
    dispatch(setSnoozedInvitation({ invitationId, snoozed: false }));
    navigate(`/acceptInvitation/${invitationId}`);
  };

  const handleClickOwnershipTransfer = (transferId) => {
    dispatch(setSnoozedOwnershipTransfer({ transferId, snoozed: false }));
    navigate(`/acceptOwnershipTransfer/${transferId}`);
  };

  const readSnoozedIds = (storageKey) => {
    try {
      return JSON.parse(localStorage.getItem(storageKey) || "[]");
    } catch (error) {
      logger.warn(
        `Failed to parse ${storageKey} from localStorage:`,
        error,
      );
      return [];
    }
  };

  const snoozedInvitations = readSnoozedIds("snoozedInvitations");
  const unsnoozedInvitations = (myInvitations || []).filter(
    (invitation) => !snoozedInvitations.includes(invitation.id),
  );
  const invitationToView =
    unsnoozedInvitations.length > 0 ? unsnoozedInvitations[0] : null;

  const snoozedOwnershipTransfers = readSnoozedIds("snoozedOwnershipTransfers");
  const unsnoozedOwnershipTransfers = (myOwnershipTransfers || []).filter(
    (transfer) => !snoozedOwnershipTransfers.includes(transfer.id),
  );
  const transferToView =
    unsnoozedOwnershipTransfers.length > 0
      ? unsnoozedOwnershipTransfers[0]
      : null;

  const orgId = currentOrganization?.id;
  const isRealOrg =
    orgId && orgId !== CRM_ORG_ID && orgId !== DEV_TOOLS_ORG_ID;
  useEffect(() => {
    if (!isRealOrg || !orgId) return;
    const unsubUnreviewedDrafts = dispatch(beginListeningToUnreviewedDraftsCount(orgId));
    return () => {
      logger.info("unsubscribing from unreviewed drafts", orgId);
      unsubUnreviewedDrafts();
    };
  }, [dispatch, orgId, isRealOrg]);

  useEffect(() => {
    if (!authState) {
      dispatch(initAuth());
    } else if (authState === "out" && !pathIsLogin) {
      navigate("/login");
    } else if (
      authState === "in" &&
      organizationsInitialized &&
      transferToView
    ) {
      navigate(`/acceptOwnershipTransfer/${transferToView.id}`);
    } else if (
      authState === "in" &&
      organizationsInitialized &&
      invitationToView
    ) {
      navigate(`/acceptInvitation/${invitationToView.id}`);
    } else if (
      authState === "in" &&
      organizationsInitialized &&
      !currentOrganization
    ) {
      // Check if last selected was CRM or Dev Tools
      const lastOrgId = localStorage.getItem("currentOrganizationId");
      if (lastOrgId === CRM_ORG_ID && user?.superAdmin) {
        navigate("/crm");
      } else if (lastOrgId === DEV_TOOLS_ORG_ID && user?.developer) {
        navigate("/dev-tools");
      } else if (organizations.length === 0 && user?.superAdmin) {
        // No orgs but is super-admin, go to CRM
        navigate("/crm");
      } else {
        navigate("/organizations/new");
      }
    } else if (authState === "in" && pathIsLogin) {
      navigate("/");
    } else if (authState === "in" && pathIsBase) {
      // Check if last selected was CRM or Dev Tools
      const lastOrgId = localStorage.getItem("currentOrganizationId");
      if (lastOrgId === CRM_ORG_ID && user?.superAdmin) {
        navigate("/crm");
      } else if (lastOrgId === DEV_TOOLS_ORG_ID && user?.developer) {
        navigate("/dev-tools");
      } else if (pageList.length > 0) {
        navigate(pageList[0].route);
      }
    }
  }, [
    dispatch,
    navigate,
    pageList,
    authState,
    pathIsLogin,
    pathIsBase,
    organizationsInitialized,
    currentOrganization,
    invitationToView,
    transferToView,
    location.pathname,
    organizations.length,
    user?.superAdmin,
    user?.developer,
  ]);

  return (
    <React.Fragment>
      {!isCheckInView && (
        <ResponsiveAppBar
          user={user}
          currentOrganization={currentOrganization}
          organizations={organizations}
          invitations={myInvitations}
          ownershipTransfers={myOwnershipTransfers}
          handleLogoutClick={handleLogoutClick}
          handleChangeOrganization={handleChangeOrganization}
          handleClickNewOrganization={handleClickNewOrganization}
          handleClickInvitation={handleClickInvitation}
          handleClickOwnershipTransfer={handleClickOwnershipTransfer}
        />
      )}
      <Outlet context={{ authState }} />
    </React.Fragment>
  );
}

export default Admin;
